import React, { useEffect, useRef, useState } from 'react';
import {
  Upload, Trash2, ChevronUp, ChevronDown, FileText, Loader2,
  CheckCircle2, XCircle, Plus, Save, Download, Eye, AlertTriangle,
} from 'lucide-react';
import { authHeaders } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GuidesPanel = () => {
  const [guides, setGuides] = useState([]);
  const [flash, setFlash] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [expandedId, setExpandedId] = useState(''); // accordion: which guide is open
  const fileInputs = useRef({});

  // Create-guide form
  const [newTitleNL, setNewTitleNL] = useState('');
  const [newTitleEN, setNewTitleEN] = useState('');
  const [newDescNL, setNewDescNL] = useState('');
  const [newDescEN, setNewDescEN] = useState('');
  const [newCover, setNewCover] = useState('');
  const [newQrOverlay, setNewQrOverlay] = useState(true);

  // Direct-PDF quick upload (top of the panel)
  const [pdfTitleNL, setPdfTitleNL] = useState('');
  const [pdfDescNL, setPdfDescNL] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);

  const refresh = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/guides`, { headers: authHeaders() });
      if (r.ok) setGuides((await r.json()).items || []);
    } catch (_) { /* keep last */ }
  };

  useEffect(() => { refresh(); }, []);

  const uploadDirectPDF = async (e) => {
    e.preventDefault();
    if (!pdfTitleNL.trim() || !pdfFile) return;
    setPdfUploading(true); setFlash(null);
    try {
      const fd = new FormData();
      fd.append('file', pdfFile);
      fd.append('title_nl', pdfTitleNL.trim());
      fd.append('description_nl', pdfDescNL.trim());
      const r = await fetch(`${API_URL}/api/admin/guides/upload-pdf`, {
        method: 'POST', headers: authHeaders(), body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Upload mislukt');
      setFlash({ type: 'success', text: `Gids "${d.title_nl}" geüpload en direct gepubliceerd op /gidsen.` });
      setPdfTitleNL(''); setPdfDescNL(''); setPdfFile(null);
      await refresh();
    } catch (err) { setFlash({ type: 'error', text: err.message }); }
    finally { setPdfUploading(false); }
  };

  const replacePDF = async (guideId, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setFlash({ type: 'error', text: 'Alleen PDF-bestanden.' });
      return;
    }
    setBusyId(guideId); setFlash(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/admin/guides/${guideId}/upload-pdf`, {
        method: 'POST', headers: authHeaders(), body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Upload mislukt');
      setFlash({ type: 'success', text: 'PDF vervangen.' });
      await refresh();
    } catch (err) { setFlash({ type: 'error', text: err.message }); }
    finally { setBusyId(''); }
  };

  const createGuide = async (e) => {
    e.preventDefault();
    if (!newTitleNL.trim()) return;
    setLoading(true); setFlash(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/guides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          title_nl: newTitleNL.trim(),
          title_en: newTitleEN.trim() || null,
          description_nl: newDescNL.trim(),
          description_en: newDescEN.trim(),
          cover_image_url: newCover.trim() || null,
          add_qr_overlay: newQrOverlay,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Aanmaken mislukt');
      setFlash({ type: 'success', text: `Gids "${d.title_nl}" aangemaakt — upload nu de pagina's.` });
      setNewTitleNL(''); setNewTitleEN(''); setNewDescNL(''); setNewDescEN(''); setNewCover('');
      await refresh();
    } catch (err) { setFlash({ type: 'error', text: err.message }); }
    finally { setLoading(false); }
  };

  const uploadPages = async (guideId, files) => {
    if (!files || !files.length) return;
    setBusyId(guideId); setFlash(null);
    try {
      // Upload sequentially so the on-disk sort_hint stays monotonic
      // and page ordering matches selection order.
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const r = await fetch(`${API_URL}/api/admin/guides/${guideId}/pages`, {
          method: 'POST', headers: authHeaders(), body: fd,
        });
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.detail || `Upload van ${file.name} mislukt`);
        }
      }
      setFlash({ type: 'success', text: `${files.length} pagina('s) geüpload.` });
      await refresh();
    } catch (err) { setFlash({ type: 'error', text: err.message }); }
    finally { setBusyId(''); }
  };

  const movePage = async (guide, idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= guide.pages.length) return;
    const next = [...guide.pages];
    [next[idx], next[target]] = [next[target], next[idx]];
    setBusyId(guide.id);
    try {
      await fetch(`${API_URL}/api/admin/guides/${guide.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ pages: next }),
      });
      await refresh();
    } catch (_) { /* ignore */ }
    finally { setBusyId(''); }
  };

  const deletePage = async (guideId, filename) => {
    if (!window.confirm('Pagina verwijderen?')) return;
    setBusyId(guideId);
    try {
      await fetch(`${API_URL}/api/admin/guides/${guideId}/pages/${filename}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      await refresh();
    } finally { setBusyId(''); }
  };

  const generatePDF = async (guide) => {
    setBusyId(guide.id); setFlash(null);
    try {
      const r = await fetch(`${API_URL}/api/admin/guides/${guide.id}/generate`, {
        method: 'POST', headers: authHeaders(),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Generatie mislukt');
      setFlash({ type: 'success', text: `PDF gegenereerd! Nu zichtbaar op /gidsen.` });
      await refresh();
    } catch (err) { setFlash({ type: 'error', text: err.message }); }
    finally { setBusyId(''); }
  };

  const deleteGuide = async (guide) => {
    if (!window.confirm(`Gids "${guide.title_nl}" en alle pagina's definitief verwijderen?`)) return;
    setBusyId(guide.id);
    try {
      await fetch(`${API_URL}/api/admin/guides/${guide.id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      await refresh();
    } finally { setBusyId(''); }
  };

  const togglePublished = async (guide) => {
    setBusyId(guide.id);
    try {
      await fetch(`${API_URL}/api/admin/guides/${guide.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ published: !guide.published }),
      });
      await refresh();
    } finally { setBusyId(''); }
  };

  const updateMeta = async (guide, field, value) => {
    await fetch(`${API_URL}/api/admin/guides/${guide.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ [field]: value }),
    });
    refresh();
  };

  return (
    <div data-testid="guides-panel">
      <div style={styles.header}>
        <h2 style={styles.h2}>Gidsen</h2>
        <p style={styles.sub}>
          Upload pagina-afbeeldingen, zet ze in de juiste volgorde en bouw je PDF.
          Elke gepubliceerde gids verschijnt automatisch op <code>/gidsen</code>.
        </p>
      </div>

      {flash && (
        <div
          style={{ ...styles.flash, ...(flash.type === 'error' ? styles.flashError : styles.flashOk) }}
          data-testid={`guides-flash-${flash.type}`}
        >
          {flash.type === 'error' ? <XCircle size={16} /> : <CheckCircle2 size={16} />}
          <span>{flash.text}</span>
        </div>
      )}

      {/* -------- QUICK PDF UPLOAD (snelste route) -------- */}
      <form onSubmit={uploadDirectPDF} style={{ ...styles.createCard, borderColor: '#C5A059' }} data-testid="guides-quick-pdf">
        <h3 style={styles.h3}>Snelupload — kant-en-klare PDF</h3>
        <p style={{ ...styles.sub, marginTop: 0, marginBottom: '0.75rem' }}>
          Heb je al een PDF? Upload hem direct — dan verschijnt de gids meteen op <code>/gidsen</code>.
        </p>
        <div style={styles.grid2}>
          <label style={styles.field}>
            <span style={styles.label}>Titel (NL) *</span>
            <input
              style={styles.input}
              value={pdfTitleNL}
              onChange={(e) => setPdfTitleNL(e.target.value)}
              placeholder="Bijv. Belastinggids Spanje"
              required
              data-testid="guides-pdf-title"
            />
          </label>
          <label style={styles.field}>
            <span style={styles.label}>PDF-bestand *</span>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
              style={styles.input}
              data-testid="guides-pdf-file"
            />
          </label>
        </div>
        <label style={styles.field}>
          <span style={styles.label}>Korte beschrijving (NL)</span>
          <textarea
            style={styles.textarea}
            value={pdfDescNL}
            onChange={(e) => setPdfDescNL(e.target.value)}
            rows={2}
            placeholder="Wat leert de lezer in deze gids?"
            data-testid="guides-pdf-desc"
          />
        </label>
        <button
          type="submit"
          style={styles.btnPrimary}
          disabled={pdfUploading || !pdfTitleNL.trim() || !pdfFile}
          data-testid="guides-pdf-upload-btn"
        >
          {pdfUploading ? <Loader2 size={16} className="spin" /> : <Upload size={16} />}
          Upload &amp; publiceer
        </button>
      </form>

      {/* -------- CREATE FORM (advanced — losse pagina-afbeeldingen) -------- */}
      <details style={{ marginBottom: '1.5rem' }} data-testid="guides-advanced-toggle">
        <summary style={{ cursor: 'pointer', fontSize: '0.85rem', color: '#585755', padding: '0.5rem 0' }}>
          Geavanceerd: gids opbouwen uit losse pagina-afbeeldingen (met automatische QR-overlay)
        </summary>
        <form onSubmit={createGuide} style={styles.createCard} data-testid="guides-create-form">
        <h3 style={styles.h3}>Nieuwe gids</h3>
        <div style={styles.grid2}>
          <label style={styles.field}>
            <span style={styles.label}>Titel (NL) *</span>
            <input
              style={styles.input}
              value={newTitleNL}
              onChange={(e) => setNewTitleNL(e.target.value)}
              placeholder="Bijv. Belastinggids Spanje"
              required
              data-testid="guides-new-title-nl"
            />
          </label>
          <label style={styles.field}>
            <span style={styles.label}>Titel (EN)</span>
            <input
              style={styles.input}
              value={newTitleEN}
              onChange={(e) => setNewTitleEN(e.target.value)}
              placeholder="Optional – Spain Tax Guide"
              data-testid="guides-new-title-en"
            />
          </label>
        </div>
        <div style={styles.grid2}>
          <label style={styles.field}>
            <span style={styles.label}>Korte beschrijving (NL)</span>
            <textarea
              style={styles.textarea}
              value={newDescNL}
              onChange={(e) => setNewDescNL(e.target.value)}
              rows={2}
              placeholder="Wat leert de lezer in deze gids?"
              data-testid="guides-new-desc-nl"
            />
          </label>
          <label style={styles.field}>
            <span style={styles.label}>Korte beschrijving (EN)</span>
            <textarea
              style={styles.textarea}
              value={newDescEN}
              onChange={(e) => setNewDescEN(e.target.value)}
              rows={2}
              placeholder="Optional"
              data-testid="guides-new-desc-en"
            />
          </label>
        </div>
        <label style={styles.field}>
          <span style={styles.label}>Cover-afbeelding URL (optioneel)</span>
          <input
            style={styles.input}
            value={newCover}
            onChange={(e) => setNewCover(e.target.value)}
            placeholder="https://…"
          />
        </label>
        <label style={styles.optinRow}>
          <input
            type="checkbox"
            checked={newQrOverlay}
            onChange={(e) => setNewQrOverlay(e.target.checked)}
            style={styles.checkbox}
          />
          <span>QR-overlay rechts-onderin élke pagina (link naar <code>/gidsen</code>)</span>
        </label>
        <button
          type="submit"
          style={styles.btnPrimary}
          disabled={loading || !newTitleNL.trim()}
          data-testid="guides-create-btn"
        >
          {loading ? <Loader2 size={16} className="spin" /> : <Plus size={16} />}
          Maak gids aan
        </button>
      </form>
      </details>

      {/* -------- GUIDES LIST -------- */}
      <h3 style={{ ...styles.h3, marginTop: '0.5rem' }}>Al jouw gidsen</h3>
      {guides.length === 0 && <p style={styles.empty}>Nog geen gidsen aangemaakt.</p>}
      {guides.map((g) => {
        const isOpen = expandedId === g.id;
        const isSystem = g.source === 'lead_magnet';
        return (
        <div key={g.id} style={styles.guideCard} data-testid={`guide-${g.id}`}>
          {/* --- Collapsed row (always visible) --- */}
          <button
            type="button"
            onClick={() => setExpandedId(isOpen ? '' : g.id)}
            style={styles.rowBtn}
            data-testid={`guide-row-${g.id}`}
          >
            <div style={styles.rowLeft}>
              {isOpen ? <ChevronDown size={18} color="#585755" /> : <ChevronUp size={18} style={{ transform: 'rotate(90deg)' }} color="#585755" />}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={styles.rowTitle}>{g.title_nl}</div>
                <div style={styles.guideMeta}>
                  {isSystem && <><span style={styles.sysPill}>Systeem</span> · </>}
                  {g.published ? <span style={styles.pubPill}>Gepubliceerd</span> : <span style={styles.draftPill}>Concept</span>}
                  {g.file_missing && <> · <span style={styles.missPill}><AlertTriangle size={11} /> PDF ontbreekt</span></>}
                  {g.source !== 'direct_pdf' && !isSystem && <> · {g.pages?.length || 0} pagina&apos;s</>}
                </div>
              </div>
            </div>
          </button>

          {/* --- Expanded editing area --- */}
          {isOpen && (
          <div style={styles.rowBody}>
            {g.file_missing && (
              <div style={{ ...styles.flash, ...styles.flashError, marginTop: 0, marginBottom: '0.75rem' }}>
                <AlertTriangle size={16} />
                <span>Het PDF-bestand ontbreekt op de server. Klik op <strong>Vervang PDF</strong> hieronder om het opnieuw te uploaden.</span>
              </div>
            )}

            <div style={styles.actionRow}>
              <label style={styles.btnGhost} data-testid={`guide-replace-pdf-label-${g.id}`}>
                <Upload size={14} /> Vervang PDF
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => replacePDF(g.id, e.target.files?.[0])}
                  style={{ display: 'none' }}
                  data-testid={`guide-replace-pdf-${g.id}`}
                />
              </label>
              {g.filename && !g.file_missing && (
                <a
                  href={`${API_URL}/api/gidsen/${g.filename}`}
                  target="_blank" rel="noreferrer"
                  style={styles.btnGhost}
                  data-testid={`guide-download-${g.id}`}
                >
                  <Download size={14} /> Bekijk PDF
                </a>
              )}
              <button
                type="button"
                style={styles.btnGhost}
                onClick={() => togglePublished(g)}
                disabled={busyId === g.id || !g.filename || g.file_missing}
                data-testid={`guide-publish-${g.id}`}
                title={g.published ? 'Verberg van /gidsen' : 'Toon op /gidsen'}
              >
                <Eye size={14} /> {g.published ? 'Verberg' : 'Publiceer'}
              </button>
              {!isSystem && (
                <button
                  type="button"
                  style={styles.btnDanger}
                  onClick={() => deleteGuide(g)}
                  disabled={busyId === g.id}
                  data-testid={`guide-delete-${g.id}`}
                >
                  <Trash2 size={14} /> Verwijder
                </button>
              )}
            </div>

            <label style={styles.field}>
              <span style={styles.label}>Titel (NL)</span>
              <input
                style={styles.input}
                defaultValue={g.title_nl}
                onBlur={(e) => e.target.value !== g.title_nl && updateMeta(g, 'title_nl', e.target.value)}
                data-testid={`guide-title-${g.id}`}
              />
            </label>

            <label style={styles.field}>
              <span style={styles.label}>Beschrijving (NL)</span>
              <textarea
                style={styles.textarea}
                defaultValue={g.description_nl}
                onBlur={(e) => e.target.value !== g.description_nl && updateMeta(g, 'description_nl', e.target.value)}
                rows={2}
                placeholder="Wat leert de lezer in deze gids?"
                data-testid={`guide-desc-${g.id}`}
              />
            </label>

            {/* Pages grid — only for guides built from page images */}
            {g.source !== 'direct_pdf' && !isSystem && (
            <>
            <div style={styles.pagesGrid}>
              {(g.pages || []).map((p, idx) => (
                <div key={p} style={styles.pageThumb} data-testid={`guide-page-${g.id}-${idx}`}>
                  <img
                    src={`${API_URL}/api/admin/guides/${g.id}/pages/${p}?t=${g.updated_at}`}
                    alt={`p${idx + 1}`}
                    style={styles.thumbImg}
                  />
                  <div style={styles.thumbOverlay}>
                    <span style={styles.thumbIdx}>{idx + 1}</span>
                    <div style={styles.thumbBtns}>
                      <button type="button" style={styles.thumbBtn} disabled={idx === 0}
                        onClick={() => movePage(g, idx, -1)} title="Naar boven"
                        data-testid={`guide-page-up-${g.id}-${idx}`}>
                        <ChevronUp size={14} />
                      </button>
                      <button type="button" style={styles.thumbBtn} disabled={idx === g.pages.length - 1}
                        onClick={() => movePage(g, idx, 1)} title="Naar beneden"
                        data-testid={`guide-page-down-${g.id}-${idx}`}>
                        <ChevronDown size={14} />
                      </button>
                      <button type="button" style={{ ...styles.thumbBtn, color: '#991B1B' }}
                        onClick={() => deletePage(g.id, p)} title="Verwijder"
                        data-testid={`guide-page-del-${g.id}-${idx}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <label style={styles.uploadTile}>
                <input
                  ref={(el) => (fileInputs.current[g.id] = el)}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => uploadPages(g.id, Array.from(e.target.files || []))}
                  style={{ display: 'none' }}
                  data-testid={`guide-upload-${g.id}`}
                />
                {busyId === g.id
                  ? <><Loader2 size={20} className="spin" /><span>Bezig...</span></>
                  : <><Upload size={20} /><span>Upload pagina&apos;s</span></>}
              </label>
            </div>

            <div style={styles.guideFooter}>
              <button
                type="button"
                style={styles.btnPrimary}
                onClick={() => generatePDF(g)}
                disabled={busyId === g.id || !(g.pages && g.pages.length)}
                data-testid={`guide-generate-${g.id}`}
              >
                {busyId === g.id ? <Loader2 size={16} className="spin" /> : <FileText size={16} />}
                Genereer PDF ({g.pages?.length || 0} pagina&apos;s)
              </button>
            </div>
            </>
            )}
          </div>
          )}
        </div>
        );
      })}

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const styles = {
  header: { marginBottom: '1.25rem' },
  h2: { fontFamily: "'Playfair Display', serif", fontSize: '1.75rem', margin: 0, color: '#1A1918' },
  h3: { fontFamily: "'Playfair Display', serif", fontSize: '1.15rem', margin: '0 0 0.75rem', color: '#1A1918' },
  sub: { color: '#585755', fontSize: '0.9rem', margin: '0.25rem 0 0' },

  flash: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.9rem' },
  flashOk: { background: '#DCFCE7', color: '#166534', border: '1px solid #86EFAC' },
  flashError: { background: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5' },

  createCard: { background: 'white', border: '1px solid #EAE5DD', padding: '1.5rem', marginBottom: '1.5rem' },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.75rem' },
  label: { fontSize: '0.75rem', fontWeight: 600, color: '#1A1918', letterSpacing: '0.03em', textTransform: 'uppercase' },
  input: { padding: '0.55rem 0.75rem', border: '1px solid #D8D3CA', fontSize: '0.95rem', background: 'white', boxSizing: 'border-box', width: '100%' },
  textarea: { padding: '0.55rem 0.75rem', border: '1px solid #D8D3CA', fontSize: '0.9rem', background: 'white', boxSizing: 'border-box', width: '100%', fontFamily: 'inherit', resize: 'vertical' },
  optinRow: { display: 'flex', gap: '0.5rem', alignItems: 'flex-start', margin: '0.5rem 0', color: '#585755', fontSize: '0.85rem', cursor: 'pointer' },
  checkbox: { marginTop: '0.15rem', width: '16px', height: '16px', accentColor: '#C5A059', flexShrink: 0 },

  btnPrimary: { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 1.15rem', background: '#1A1918', color: '#C5A059', border: 'none', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer' },
  btnGhost: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.85rem', background: 'white', color: '#1A1918', border: '1px solid #D8D3CA', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' },
  btnDanger: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.85rem', background: 'white', color: '#991B1B', border: '1px solid #FCA5A5', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' },

  empty: { color: '#8C8B88', fontSize: '0.9rem', textAlign: 'center', padding: '2rem 0' },

  guideCard: { background: 'white', border: '1px solid #EAE5DD', marginBottom: '0.5rem' },
  guideHeader: { display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap' },
  rowBtn: { display: 'flex', alignItems: 'center', width: '100%', padding: '0.9rem 1.1rem', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' },
  rowLeft: { display: 'flex', gap: '0.6rem', alignItems: 'center', flex: 1, minWidth: 0 },
  rowTitle: { fontFamily: "'Playfair Display', serif", fontSize: '1.05rem', color: '#1A1918', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  rowBody: { padding: '0 1.1rem 1.1rem', borderTop: '1px solid #EAE5DD' },
  actionRow: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap', margin: '0.9rem 0' },
  sysPill: { padding: '0.1rem 0.5rem', background: '#EAE5DD', color: '#1A1918', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' },
  draftPill: { padding: '0.1rem 0.5rem', background: '#FEF3C7', color: '#92400E', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' },
  missPill: { padding: '0.1rem 0.5rem', background: '#FEE2E2', color: '#991B1B', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' },
  guideMeta: { fontSize: '0.8rem', color: '#8C8B88', marginTop: '0.35rem', display: 'flex', gap: '0.25rem', alignItems: 'center', flexWrap: 'wrap' },
  metaLink: { color: '#C5A059', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' },
  pubPill: { padding: '0.1rem 0.5rem', background: '#DCFCE7', color: '#166534', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' },
  actionCol: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap' },

  pagesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.5rem', margin: '1rem 0' },
  pageThumb: { position: 'relative', aspectRatio: '3/4', overflow: 'hidden', border: '1px solid #EAE5DD', background: '#FAF9F6' },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  thumbOverlay: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '0.3rem', background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.6) 100%)' },
  thumbIdx: { alignSelf: 'flex-start', background: 'rgba(26,25,24,0.85)', color: '#C5A059', padding: '0.15rem 0.4rem', fontSize: '0.7rem', fontWeight: 700 },
  thumbBtns: { display: 'flex', gap: '0.2rem', justifyContent: 'center' },
  thumbBtn: { width: '26px', height: '26px', border: 'none', background: 'rgba(255,255,255,0.92)', color: '#1A1918', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  uploadTile: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', aspectRatio: '3/4', border: '2px dashed #D8D3CA', background: '#FAF9F6', color: '#8C6E28', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 },
  guideFooter: { marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end' },
};

export default GuidesPanel;
